import { Spin } from 'antd'
import {
  LABEL_WIDTH,
  SectionContainer,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { computeMtCylindrical } from 'features/indicationTriage/cards/ffsCalc/foliasProfileLookup'
import _ from 'lodash'
import React, { Dispatch, FC, SetStateAction, useMemo, useState } from 'react'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import {
  CurveDataProps,
  FfsAcceptabilityCriteriaCurve,
} from './FfsAcceptabilityCriteriaCurve'
import { FlawSizeCriteriaCheckResult } from './FfsFlawSizeCriteriaSection'

const LABEL_FOLIAS_FACTOR = (
  <>
    M<sub>t</sub>
  </>
)

export const LABEL_RSFA = (
  <>
    RSF<sub>a</sub>
  </>
)

export interface FfsFinalAcceptabilityCriteraSectionValues {
  foliasFactor: GeckoMeasurement | null
  rsfa: GeckoMeasurement | null
  setRsfa: Dispatch<SetStateAction<GeckoMeasurement | null>>
  rsf: GeckoMeasurement | null
  rt: GeckoMeasurement | null
}

interface HookProps {
  lambda: GeckoMeasurement | null
  flawSizeCriteriaCheckValues:
    | FlawSizeCriteriaCheckResult['criteriaValues']
    | null
}

export function useLevelOneFinalAcceptabilityCriteriaValues({
  lambda,
  flawSizeCriteriaCheckValues,
}: HookProps): FfsFinalAcceptabilityCriteraSectionValues {
  const [rsfa, setRsfa] = useState<GeckoMeasurement | null>(
    GeckoMeasurement.CreateFromValue(0.9, GeckoMeasurementUnit.Ratio)
  )
  const foliasFactor = useMemo(
    () =>
      lambda
        ? GeckoMeasurement.CreateFromValue(
            computeMtCylindrical(lambda.num),
            GeckoMeasurementUnit.Ratio
          )
        : null,
    [lambda]
  )

  const rt = flawSizeCriteriaCheckValues?.remainingThicknessRatio ?? null
  const rsf = useMemo<GeckoMeasurement | null>(() => {
    const mt = foliasFactor
    if (_.isNil(rt) || _.isNil(mt)) {
      return null
    }
    return GeckoMeasurement.CreateFromValue(
      rt.num / (1 - (1 / mt.num) * (1 - rt.num)),
      GeckoMeasurementUnit.Ratio
    )
  }, [foliasFactor, rt])

  return useMemo(
    () => ({
      foliasFactor,
      rsfa,
      setRsfa,
      rsf,
      rt,
    }),
    [foliasFactor, rsf, rsfa, rt]
  )
}

type FfsFinalAcceptabilityCriteraSectionProps =
  FfsFinalAcceptabilityCriteraSectionValues & {
    lambda: GeckoMeasurement | null
    loading: boolean
    curveDataProps: CurveDataProps
  }

const RawFfsFinalAcceptabilityCriteraSection: FC<
  FfsFinalAcceptabilityCriteraSectionProps
> = ({
  foliasFactor,
  loading,
  rsfa,
  setRsfa,
  lambda,
  rsf,
  curveDataProps,
  rt,
}) => {
  return (
    <Spin spinning={loading}>
      <SectionContainer>
        <FfsParameterField
          label={LABEL_RSFA}
          labelTooltip='Acceptable Remaining Strength Factor'
          labelWidth={LABEL_WIDTH}
          value={rsfa}
          setValue={setRsfa}
          measurementType='ratio'
          min={GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.Ratio)}
          max={GeckoMeasurement.CreateFromValue(1, GeckoMeasurementUnit.Ratio)}
          step={0.05}
        />
        <FfsParameterField
          label={LABEL_FOLIAS_FACTOR}
          labelTooltip='Folias Factor'
          labelWidth={LABEL_WIDTH}
          value={foliasFactor}
          measurementType='ratio'
        />
        <FfsParameterField
          label='RSF'
          labelTooltip='Remaining Strength Factor'
          labelWidth={LABEL_WIDTH}
          value={rsf}
          measurementType='ratio'
        />
        {!_.isNil(lambda) &&
          !_.isNil(foliasFactor) &&
          !_.isNil(curveDataProps.curveData) &&
          !_.isNil(rsf) && (
            <FfsAcceptabilityCriteriaCurve
              {...curveDataProps}
              lambda={lambda.num}
              rsf={rsf.num}
              rt={rt?.num ?? null}
            />
          )}
      </SectionContainer>
    </Spin>
  )
}

export const FfsFinalAcceptabilityCriteraSection = React.memo(
  RawFfsFinalAcceptabilityCriteraSection
)
